import React, {useState, useEffect} from 'react'
import { 
    Layout,
    Col, 
    Row,
    Button,
    Select,
    Table,
    Space,
    DatePicker,
} from 'antd';
import { ProductOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Navbar from '../../Components/Navbar/Navbar'
import { UseGetRequest } from '../../Utils/api/ApiRequests'
import BtnExcel from '../../Components/BtnExcel';
import { 
  getTodayDateISO, 
  formatAmount,
  getPropertyId, 
  getOwnerId,
  getUnitId,
  getStartDate, 
  getEndDate,
  getOwnerType,
} from '../../Utils/Common';

const { Option } = Select;
const { RangePicker } = DatePicker;

const MonthlyOwnerIncome = () => {
  const navigate = useNavigate();
  const asynch_property = getPropertyId();
  const asynch_unit = getUnitId();
  const asynch_owner = getOwnerId();
  const asynch_type = getOwnerType();
  const asynch_start = getStartDate();
  const asynch_end = getEndDate();

  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pageSize, setPageSize] = useState('');
  const [income, setIncome] = useState(0);
  const [expense, setExpense] = useState(0);
  const [netIncome, setNetIncome] = useState(0);
  const [excelData, setExcelData] = useState([]);
  const [receivableData, setReceivableData] = useState([]);
  const [propertyData, setPropertyData] = useState([]);
  const [ownerData, setOwnerData] = useState([]);
  const [rentaUnitData, setRentalUnitData] = useState([]);
  const startDate = dayjs().startOf('month');
  const endDate = dayjs().endOf('month');
  const customSpinner = <ProductOutlined style={{ fontSize: 24 }} spin />;
  const [filterConfig, setFilterConfig] = useState({
    start_date: asynch_start ? asynch_start : dayjs(startDate).format('YYYY-MM-DD'),
    end_date: asynch_end ? asynch_end : dayjs(endDate).format('YYYY-MM-DD'),
    property_id: asynch_property || '',
    rental_unit_id: asynch_unit || '',
    owner_id: asynch_owner || '',
    type: asynch_type || '',
    page: 1,
  })

  const owner_type = [
    {
      value: 'rental',
      label: 'Rental Unit',
    },
    {
      value: 'parking',
      label: 'Parking Unit',
    },
  ]

  const defaultColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status) => status === 'unpaid' ? 'Pending' : 'Deposited'
    },
    {
      title: 'Period Covered',
      dataIndex: 'billing_date',
      align: 'center',
    },
    {
      title: 'Owner Name',
      dataIndex: 'owner_name',
      align: 'center',
    },
    {
      title: 'Property',
      dataIndex: 'concatenated_property',
      align: 'center',
    },
    
    {
      title: 'Renter Name',
      dataIndex: 'tenant_name',
      align: 'center',
    },
    {
      title: 'Tenant Billing Numbers',
      dataIndex: 'billing_no',
      align: 'center',
      render: (_, record) => {
        return (
          <Button 
            className='mb-2' onClick={() => navigate('/preview-billing/tenant/' + record.billing_no)}
          >
            Billing No. {record.billing_no}
          </Button>
        );
      },
    },
    {
      title: 'Income Amount',
      dataIndex: 'income_amount',
      align: 'right',
      render: (income_amount) => formatAmount(income_amount),
    },
    {
      title: 'Owner Expense Billing Number',
      dataIndex: 'owner_expense',
      align: 'center',
      render: (_, record) => (
        record?.owner_expenses?.map((data) => (
          <Button 
            key={data.owner_billing}
            className='mb-2' 
            onClick={() => navigate('/preview-billing/owner/' + data.owner_billing)}
          >
            Billing No. {data.owner_billing}
          </Button>
        ))
      ),
    },
    {
      title: 'Expense Amount',
      dataIndex: 'expense_amount',
      align: 'right',
      render: (expense_amount) => formatAmount(expense_amount),
    },
    {
      title: 'Total Net Income',
      dataIndex: 'total_net_income',
      align: 'right',
      render: (total_net_income) => formatAmount(total_net_income),
    },
    {
      title: '',
      dataIndex: '',
      render: (_, record) => (
        <span className='center'>
          <Button 
            className='button-color btn-header' 
            type="primary"
            onClick={() => navigate('/owner-income/preview/' + record.type + '/' + record.unit_id + '/' + record.billing_no)}
          >
            View
          </Button>
        </span>
      ),
    },
  ];

  async function fetchReceivableData() {
    setIsLoading(true);
    const response = await UseGetRequest("payments/owner_monthly_income", filterConfig);
    if (response.data) {
      setIncome(response?.data?.total_income ? formatAmount(response?.data?.total_income) : 0)
      setExpense(response?.data?.total_expense ? formatAmount(response?.data?.total_expense) : 0)
      setNetIncome(response?.data?.total_net_pay ? formatAmount(response?.data?.total_net_pay) : 0)
      setReceivableData(response?.data?.data);
      setPageSize(response?.data?.size)
    } else {
      setIncome(0)
      setExpense(0)
      setNetIncome(0)
      setReceivableData([])
      setPageSize('')
    }
    setIsLoading(false);
  }

  async function fetchExportData() {
    setExportLoading(true);
    const response = await UseGetRequest("payments/owner_monthly_income", 
      {
        start_date: filterConfig.start_date,
        end_date: filterConfig.end_date,
        rental_unit_id: filterConfig.rental_unit_id,
        property_id: filterConfig.property_id,
        owner_id: filterConfig.owner_id,
        type: filterConfig.type
      }
    );
    if (response.data) {
      const data = response?.data?.data?.map((data) => ({
        // rental_unit_id: data.rental_unit_id,
        billing_date: data.billing_date,
        // owner_id: data.owner_id,
        owner_name: data.owner_name,
        concatenated_property: data.concatenated_property,
        tenant_name: data.tenant_name,
        billing_no:  data.billing_no,
        income_amount: data.income_amount && formatAmount(data.income_amount),
        // owner_expense: data.owner_expenses.map((data) => data.owner_billing).join(",") ,
        owner_expense: data.owner_expenses?.length 
          ? data.owner_expenses.map((data) => data?.owner_billing).join(",") 
          : "",
        expense_amount: data.expense_amount && formatAmount(data.expense_amount),
        total_net_income: data.total_net_income,
        status: data.status,
      }))

      setExcelData(data)
      setExportLoading(false);
    } else {
      setExcelData([])
      setExportLoading(false);
    }
  }

  async function fetchOwnerData() {
    const response = await UseGetRequest("owners/get", 
      {
        property_id: filterConfig.property_id, 
        rental_unit_id: filterConfig.rental_unit_id, 
      });
    if (response?.data) {
      setOwnerData(response?.data?.data);
    } else {
      setOwnerData([])
    }
  }

  async function fetchPropertyData() {
    const response = await UseGetRequest("properties/get",  
      {
        property_id: '', 
        owner_id: filterConfig.owner_id, 
      });
    if (response?.data) {
      setPropertyData(response?.data?.data);
    } else {
      setPropertyData([])
    }
  }

  async function fetchUnitData() {
    const response = await UseGetRequest("rental_units/get",  
      {
        property_id: filterConfig.property_id, 
        owner_id: filterConfig.owner_id, 
      });
    if (response?.data) {
      setRentalUnitData(response?.data?.data);
    } else {
      setRentalUnitData([])
    }
  }

  const onSearchOwner = (e) => {
    setFilterConfig((prev) => (
      { 
        ...prev,
        owner_id: e || '',
        property_id: '',
        rental_unit_id: '',
        page: 1,
      }
    ));
    if(e){
      localStorage.setItem('owner_id', JSON.stringify(e));

    } else {
      localStorage.removeItem('owner_id');
    }
  };

  const onSearchProperty = (e) => {
    setFilterConfig((prev) => (
      { 
        ...prev,
        property_id: e || '',
        page: 1,
      }
    ));
    if(e){
      localStorage.setItem('property_id', JSON.stringify(e));

    } else {
      localStorage.removeItem('property_id');
    }
  };

  const onSearchType = (e) => {
    setFilterConfig((prev) => (
      { 
        ...prev,
        type: e || '',
        page: 1,
      }
    ));
    if(e){
      localStorage.setItem('type', JSON.stringify(e));

    } else {
      localStorage.removeItem('type');
    }
  };

  const onSearchUnit = (e) => {
    const newData = rentaUnitData.filter((item) => item.id === e);
    setFilterConfig((prev) => (
      { 
        ...prev,
        rental_unit_id: e || '',
        property_id: newData[0]?.property_id,
        owner_id: newData[0]?.owner_id,
        page: 1,
      }
    ));
    if(e){
      localStorage.setItem('rental_unit_id', JSON.stringify(e));

    } else {
      localStorage.removeItem('rental_unit_id');
    }
  };

  const onSearchDate = (e) => {
    if(e){
     setFilterConfig((prev) => (
       {
         ...prev,
         start_date: e[0].format("YYYY-MM-DD"),
         end_date: e[1].format("YYYY-MM-DD"),
         page: 1,
       }
     ));
     localStorage.setItem('start_date', JSON.stringify(e[0].format("YYYY-MM-DD")));
     localStorage.setItem('end_date', JSON.stringify(e[1].format("YYYY-MM-DD")));
    } else {
      setFilterConfig((prev) => (
        {
          ...prev,
          start_date: '',
          end_date: '',
          page: 1,
        }
      ));
      localStorage.removeItem('start_date');
      localStorage.removeItem('end_date');
    }
  };

  const handleTableChange = (pagination) => {
    setFilterConfig((prev) => ({
      ...prev,
      page: parseInt(pagination.current),
      page_size: pagination.pageSize
      // total_records_per_page: pagination.pageSize,
    }));
  };

  const pageSizeOptions = ['10', '20', '50', '100', `${pageSize}`];
  const sortedPageSizeOptions = pageSizeOptions.filter(item => item <= pageSize).sort((a, b) => Number(a) - Number(b)); 

  useEffect(() => {
    fetchReceivableData();
    fetchExportData();
    fetchPropertyData();
    fetchOwnerData()
    fetchUnitData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className='page'>
      <Navbar navKey={'owner_income_nav'}/>
      <Layout className='content-container overflow-y'>
        <Row className='custom-padding responsive-inline responsive-tab-inline'>
          <Col span={12} className='header-container'>
            OWNER INCOME
          </Col>
          <Col span={12} className='mt-2 responsive-tab-width' align='end'>
            <Space className='responsive-inline'>
              <BtnExcel
                label={"Export to Excel"}
                size={"sm"}
                excelData={excelData}
                xlsxHeaders={[
                  // "Unit Id",
                  "Billing Date",
                  // "Owner Id",
                  "Owner Name",
                  "Property & Building",
                  "Tenant Name",
                  "Billing Number",
                  "Income Amount",
                  "Owner Expense Billing Number",
                  "Expense Amount",
                  "Net Income",
                  "Status",
                ]}
                handleData={fetchExportData}
                isClicked={exportLoading}
                fileName={`Monthly_Owner_Income_${getTodayDateISO()}.xlsx`}
              />
            </Space>
          </Col>
        </Row>
        <Row className='ms-4 mb-2'>
          <Space lassName='responsive-inline excel-row'>
            <Select 
              placeholder="Search Property"
              className='select-field responsive-header-input'
              onChange={(e) => onSearchProperty(e)}
              showSearch
              allowClear
              value={filterConfig.property_id}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option key='all' value=''>All Property</Option>
              {propertyData.map(month => (
                <Option key={month.id} value={month.id}>{month.name}</Option>
              ))}
            </Select>
            <Select 
              placeholder="Search Type"
              className='select-field responsive-header-input'
              onChange={(e) => onSearchType(e)}
              showSearch
              allowClear
              value={filterConfig.type}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option key='all' value=''>All Type</Option>
              {owner_type.map(month => (
                <Option key={month.value} value={month.value}>{month.label}</Option>
              ))}
            </Select>
            <Select 
              placeholder="Search Rental Unit"
              className='select-field responsive-header-input'
              onChange={(e) => onSearchUnit(e)}
              showSearch
              allowClear
              value={filterConfig.rental_unit_id}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option key='all' value=''>All Rental Units</Option>
              {rentaUnitData.map(month => (
                <Option key={month.id} value={month.id}>{month.name}</Option>
              ))}
            </Select>
            <Select 
              placeholder="Search Owner"
              className='select-field responsive-header-input'
              onChange={(e) => onSearchOwner(e)}
              showSearch
              allowClear
              value={filterConfig.owner_id}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option key='all' value=''>All Owner</Option>
              {ownerData.map(month => (
                <Option key={month.id} value={month.id}>{month.name}</Option>
              ))}
            </Select>
            <RangePicker 
              renderExtraFooter={() => 'extra footer'}
              className='responsive-header-input'
              onChange={(e)=>onSearchDate(e)}
              allowClear
              value={[
                filterConfig?.start_date ? dayjs(filterConfig.start_date) : null,
                filterConfig?.end_date ? dayjs(filterConfig.end_date) : null
              ]}
            />
          </Space>
        </Row>
        <Row gutter={[16, 16]} className='ms-4'>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Total Income: {income}
          </Col>
      
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Expense: {expense}
          </Col>
    
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Net Income: {netIncome}
          </Col>
        </Row>
        <Row className='page-content'>
          <Col span={24}>
            <Table
              rowClassName={() => 'editable-row'}
              bordered
              dataSource={receivableData}
              columns={defaultColumns}
              loading={{
                indicator: customSpinner,
                spinning: isLoading,
              }}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: sortedPageSizeOptions,
                showSizeChanger: true,
                total: pageSize,
                current: filterConfig?.page,
                pageSize: filterConfig?.total_records_per_page,
              }}
              onChange={handleTableChange}
            /> 
          </Col>
        </Row>
      </Layout>
    </Layout>
  )
}

export default MonthlyOwnerIncome
